import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { SearchableLink } from '@/components/Navigation.shared'

const RECENT_PAGES_STORAGE_KEY = 'city-sense.recent-pages'
/** Cap on persisted entries to bound localStorage growth. */
const MAX_TRACKED_PAGES = 10

export type VisitStat = {
  /** Canonical searchable link, e.g. '/vehicles'. */
  path: string
  count: number
  /** Epoch ms of the most recent visit. */
  lastVisit: number
}

interface RecentPagesState {
  visits: Record<string, VisitStat>
}

interface RecentPagesActions {
  recordVisit: (canonicalPath: string) => void
  getTopLinks: (allLinks: SearchableLink[], n: number) => SearchableLink[]
}

type RecentPagesStore = RecentPagesState & RecentPagesActions

/**
 * Rank visits by frequency, breaking ties by recency. Pure for testability.
 */
export const rankVisits = (visits: Record<string, VisitStat>): VisitStat[] =>
  Object.values(visits).sort((a, b) => b.count - a.count || b.lastVisit - a.lastVisit)

export const useRecentPagesStore = create<RecentPagesStore>()(
  persist(
    (set, get) => ({
      visits: {},

      recordVisit: (canonicalPath: string) => {
        set(state => {
          const existing = state.visits[canonicalPath]
          const updated: Record<string, VisitStat> = {
            ...state.visits,
            [canonicalPath]: {
              path: canonicalPath,
              count: (existing?.count ?? 0) + 1,
              lastVisit: Date.now(),
            },
          }

          // Evict the lowest-ranked entries when over the cap.
          const ranked = rankVisits(updated)
          if (ranked.length <= MAX_TRACKED_PAGES) {
            return { visits: updated }
          }
          const kept: Record<string, VisitStat> = {}
          for (const stat of ranked.slice(0, MAX_TRACKED_PAGES)) {
            kept[stat.path] = stat
          }
          return { visits: kept }
        })
      },

      getTopLinks: (allLinks: SearchableLink[], n: number) => {
        const byLink = new Map(allLinks.map(link => [link.link, link]))
        const result: SearchableLink[] = []
        for (const stat of rankVisits(get().visits)) {
          const link = byLink.get(stat.path)
          // Intersect with live, permission-filtered links so lost-access pages drop out.
          if (link) {
            result.push(link)
            if (result.length >= n) break
          }
        }
        return result
      },
    }),
    {
      name: RECENT_PAGES_STORAGE_KEY,
      partialize: state => ({ visits: state.visits }),
    }
  )
)
